import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from "../../../../../select/Select";
import { stringDateToPPP } from "../../../../../../util/date";
import { defaultIfNullish } from "../../../../../../util/stringUtil";
import { FormikDatePicker } from "../../../../../formcontrols/DatePicker";
import { DisplayItem } from "../../../../../formcontrols/DisplayItem";
import { SelectFormik } from "../../../../../formcontrols/Select";
import { useQuery } from "@tanstack/react-query";
import { get } from "../../../../../../api/util";
import { FormikProps } from "formik";
import FirmaEditAddressModal from "./FirmaEditAddressModal";
import FirmaStamCVRModal from "./FirmaStamCVRModal";
import { ModalProvider } from "../../../../../Modal/Modal";
import { Adresse, FirmaDto } from "../../../../../../interfaces/schema";
import { FirmaFields } from "./StamoplysningerTop";
import { TextInputFormik } from "../../../../../formcontrols/InputItem";
import Spinner from "../../../../../spinner/Spinner";
import FirmaStamPnrModal from "./FirmaStamPnrModal";
import { cvrNrStatusMapper } from "../../../../../../util/cvrStatusMapper";
import {
  VAT_REQUIRED_MESSAGE,
  isDanishCvrNumber,
  requiresVatNumber,
} from "../../../../../../util/vatValidation";
import {
  getFirmaRegistrationNumber,
  getFirmaRegistrationNumberLabel,
  hasFirmaRegistrationNumber,
} from "../../../../../../util/firmaRegistrationNumber";

const FirmaStamMeta1 = ({
  firma,
  formik,
  editMode,
}: {
  firma: FirmaDto;
  formik: FormikProps<FirmaFields>;
  editMode: boolean;
}) => {
  const {
    data: restFirma,
    isLoading,
    error: error3,
  } = useQuery<FirmaDto>({
    // Firma-id i nøglen, ellers vises et tidligere besøgt firmas virksomhedsform (cache).
    queryKey: ["virksomhedsformFirma", firma.id],
    queryFn: function () {
      return get({ query: `firma/${firma.id}` });
    },
  });
  const { data: kodelisteData, error: error1 } = useQuery({
    queryKey: ["virksomhedsformer"],
    queryFn: function () {
      return get<
        {
          VirksomhedsformKode: IVirksomhedsformKode[];
        }[]
      >({
        query: "kodelister",
      });
    },
  });
  const { data: selskabstyper, error: error2 } = useQuery<
    { navn: string; nr: number }[]
  >({
    queryKey: ["selskabstyper"],
    queryFn: function () {
      return get({
        query: "kodelister/selskabstype",
      });
    },
  });
  if (error1 || error2 || error3) return <div>Fejl</div>;
  if (isLoading) return <Spinner />;
  if (!restFirma) return null;

  const virksomhedsformer: IVirksomhedsformKode[] = kodelisteData
    ? kodelisteData["VirksomhedsformKode"]
    : null;
  const selskabsSelect = selskabstyper
    ? [{ nr: -1, navn: "Ingen" }, ...selskabstyper]
    : [];
  const virksomhedsformNavn = restFirma.virksomhedsform
    ? virksomhedsformer
      ? virksomhedsformer.filter((sf) => {
          return sf.id === restFirma.virksomhedsform;
        })[0].navn
      : "Henter virksomhedsform ..."
    : "-";
  const selskabstype = selskabstyper
    ? formik.values.selskabstype
      ? (selskabstyper.filter((e) => e.nr === firma.selskabstype)[0]?.navn ??
        "-")
      : "-"
    : "Henter selskabstype ...";
  const setCurrentBeliggenhedsAdresse = (adresse: Adresse) => {
    formik.setFieldValue("beliggenhedsadresse", adresse, true);
  };
  const registrationSource = { ...firma, ...formik.values };
  const hasDanishCvr = isDanishCvrNumber(formik.values.cvrNr);
  const adressIsEditable =
    editMode &&
    (!hasFirmaRegistrationNumber(registrationSource) || !hasDanishCvr);
  const canAddPnr =
    editMode && isDanishCvrNumber(firma.cvrNr) && firma.pnr === null;
  const registrationNumber = getFirmaRegistrationNumber(registrationSource);
  const registrationNumberLabel =
    getFirmaRegistrationNumberLabel(registrationSource);
  // "Færdig" i CVR-/VAT-modalen lægger kun nummeret ind i formularen — først "Gem" nederst på siden sender det.
  const registrationNumberUnsaved =
    editMode &&
    String(registrationNumber ?? "") !==
      String(getFirmaRegistrationNumber(firma) ?? "");
  const vatMissing = requiresVatNumber({
    landekode: (formik.values.beliggenhedsadresse ?? firma.beliggenhedsadresse)
      ?.landekode,
    cvrNr: registrationSource.cvrNr,
    udenlandskVirksomhedsId: registrationSource.udenlandskVirksomhedsId,
  });
  return (
    <div className="c-item-list c-card">
      {editMode && !hasFirmaRegistrationNumber(registrationSource) ? (
        <div>
          <TextInputFormik
            config={{
              component: "textinput",
              id: "navn1",
              initialValue: firma.navn1 ?? "",
              label: "Firmanavn",
            }}
            formik={formik}
          />
        </div>
      ) : (
        <div className="flex-100">
          <DisplayItem
            label="Firmanavn"
            text={firma.navn1}
            isEditable={!hasFirmaRegistrationNumber(registrationSource)}
          />
        </div>
      )}
      <ModalProvider>
        <FirmaEditAddressModal
          adressIsEditable={adressIsEditable}
          firma={firma}
          currentBeliggenhedsAdresse={formik.values.beliggenhedsadresse}
          setCurrentBeliggenhedsAdresse={setCurrentBeliggenhedsAdresse}
          kommuneNr={firma.kommuneNr ?? undefined}
          retskredsNr={firma.retskredsNr ?? undefined}
          setKommuneNr={(e) => formik.setFieldValue("kommuneNr", e, true)}
          setRetskredsNr={(e) => formik.setFieldValue("retskredsNr", e, true)}
        />
      </ModalProvider>
      {!adressIsEditable && (
        <div className="flex-100">
          <h5 className="form-label">Adresse</h5>
          <div
            className={`form-control--value${
              hasFirmaRegistrationNumber(registrationSource)
                ? ""
                : " is-editable"
            }`}
          >
            {formik.values.beliggenhedsadresse?.toLines.map((e) => (
              <p key={e} className="my-1">
                {e}
              </p>
            ))}
          </div>
        </div>
      )}
      <div>
        <h5 className="form-label">{registrationNumberLabel}</h5>
        <ModalProvider>
          <FirmaStamCVRModal editMode={editMode} firma={firma} main={formik} />
        </ModalProvider>
        {!editMode && (
          <div
            className={`form-control--value${
              hasFirmaRegistrationNumber(registrationSource)
                ? ""
                : " is-editable"
            }`}
          >
            {registrationNumber ?? "-"}
          </div>
        )}
        {registrationNumberUnsaved && (
          <p className="text-danger fw-semibold mt-1 mb-0" role="status">
            Ikke gemt endnu — tryk Gem nederst på siden
          </p>
        )}
        {vatMissing && (
          <p className="text-danger mt-1 mb-0" role="alert">
            {VAT_REQUIRED_MESSAGE}
          </p>
        )}
      </div>
      <div>
        {canAddPnr ? (
          <ModalProvider>
            <label className="form-label">P-nr.</label>
            <FirmaStamPnrModal
              cvrNr={Number(formik.values.cvrNr)}
              formik={formik}
            />
          </ModalProvider>
        ) : (
          <DisplayItem label="P-nr." text={formik.values.pnr} />
        )}
      </div>
      <div>
        <DisplayItem
          label="Virksomhedsform"
          text={defaultIfNullish(virksomhedsformNavn)}
        />
      </div>
      <div>
        {editMode && (
          <>
            <label className="form-label">Advokatvirksomhed</label>
            <Select
              defaultValue={firma.advokatVirksomhed ? "true" : "false"}
              onValueChange={(e) =>
                formik.setFieldValue("advokatVirksomhed", e === "true")
              }
            >
              <SelectTrigger
                aria-label="Advokatvirksomhed"
                placeholder="Vælg..."
              />
              <SelectContent>
                <SelectItem value="true">Ja</SelectItem>
                <SelectItem value="false">Nej</SelectItem>
              </SelectContent>
            </Select>
          </>
        )}
        {!editMode && (
          <DisplayItem
            label="Advokatvirksomhed"
            text={firma.advokatVirksomhed ? "Ja" : "Nej"}
            isEditable={true}
          />
        )}
      </div>
      <div className="flex-100">
        {editMode && (
          <SelectFormik
            config={{
              component: "select",
              id: "selskabstype",
              label: "Selskabstype",
              placeholder: "Vælg ...",
            }}
            data={selskabsSelect.map((e) => {
              return {
                label: e.navn,
                value: e.nr.toString(),
              };
            })}
            formik={formik}
          />
        )}
        {!editMode && (
          <DisplayItem
            label="Selskabstype"
            text={defaultIfNullish(selskabstype)}
            isEditable={true}
          />
        )}
      </div>
      <div>
        {editMode && (
          <FormikDatePicker
            placeholder="dd.mm.åååå"
            formik={formik}
            config={{
              component: "datepicker",
              id: "periodeFraDato",
              label: "Startdato",
              initialValue: firma.periode?.fraDato,
            }}
          />
        )}
        {!editMode && (
          <DisplayItem
            label="Startdato"
            text={
              firma.periode?.fraDato
                ? stringDateToPPP(firma.periode?.fraDato)
                : null
            }
            isEditable={true}
          />
        )}
      </div>
      <div>
        {editMode && (
          <FormikDatePicker
            placeholder="dd.mm.åååå"
            formik={formik}
            config={{
              component: "datepicker",
              id: "periodeTilDatoInklusiv",
              label: "Ophørsdato",
              initialValue: firma.periode?.tilDatoInklusiv,
            }}
          />
        )}
        {!editMode && (
          <DisplayItem
            label="Ophørsdato"
            text={
              firma.periode?.tilDatoInklusiv
                ? stringDateToPPP(firma.periode?.tilDatoInklusiv)
                : null
            }
            isEditable={true}
          />
        )}
      </div>
      <div>
        <DisplayItem
          label="CVR-status for virksomhed"
          text={
            cvrNrStatusMapper.find(
              (status) => status.value === firma.cvrNrStatusCvr,
            )?.displayName ?? ""
          }
        />
      </div>
      {firma.pnrStatusCvr && (
        <div>
          <DisplayItem
            label="CVR-status for produktionsenhed"
            text={firma.pnrStatusCvr}
          />
        </div>
      )}
      {firma.pnrOphoertCvr && (
        <div v-if="firma.pnrOphoertCvr">
          <DisplayItem
            label="CVR ophørsdato for produktionsenhed"
            text={
              firma.pnrOphoertCvr ? stringDateToPPP(firma.pnrOphoertCvr) : null
            }
          />
        </div>
      )}
    </div>
  );
};

export default FirmaStamMeta1;

interface IVirksomhedsformKode {
  type: string;
  id: string;
  nr: number;
  navn: string;
  beskrivelse: string | null;
}
